/**
 * Constructores de DOM. Sin plantillas de cadena y sin `innerHTML`.
 *
 * No es preferencia de estilo. Esta pantalla pinta texto que llego por correo de
 * un proveedor --nombres de carrier, descripciones de cargo, condiciones
 * comerciales-- y ese texto no esta saneado en ningun punto de la cadena, a
 * proposito: el literal se conserva tal como llego [CLAUDE.md, Literales]. Con
 * `innerHTML`, un `descripcion_literal` con un `<` dentro deja de ser texto.
 * `textContent` lo pinta como lo que es.
 */

export interface Opciones {
  readonly clase?: string;
  readonly texto?: string;
  readonly attrs?: Record<string, string>;
  readonly onClick?: (ev: Event) => void;
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  opciones: Opciones = {},
  hijos: readonly (Node | null | undefined)[] = [],
): HTMLElementTagNameMap[K] {
  const nodo = document.createElement(tag);
  if (opciones.clase !== undefined) nodo.className = opciones.clase;
  if (opciones.texto !== undefined) nodo.textContent = opciones.texto;
  for (const [k, v] of Object.entries(opciones.attrs ?? {})) nodo.setAttribute(k, v);
  if (opciones.onClick !== undefined) nodo.addEventListener('click', opciones.onClick);
  for (const hijo of hijos) if (hijo != null) nodo.appendChild(hijo);
  return nodo;
}

export function texto(t: string, clase?: string): HTMLElement {
  return el('span', { clase, texto: t });
}

/**
 * Un literal del proveedor.
 *
 * [CLAUDE.md, Idioma, categoria 2] Se pinta tal como llego, sin traducir ni
 * limpiar, en cualquier idioma o alfabeto. `dir="auto"` porque la muestra traia
 * correos en chino y nada garantiza que no llegue arabe; `lang` no se declara
 * porque no se sabe cual es y declararlo mal hace que un lector de pantalla lo
 * pronuncie como si fuera del idioma de la interfaz.
 *
 * Devuelve `null` cuando no hay literal, para poder pasarlo como hijo directo.
 */
export function literal(
  t: string | null | undefined,
  clase = 'literal',
): HTMLElement | null {
  if (t === null || t === undefined || t.trim() === '') return null;
  return el('span', { clase, texto: t, attrs: { dir: 'auto' } });
}

/** Una etiqueta con su valor. La unidad de casi toda la pantalla. */
export function dato(
  etiqueta: string,
  valor: Node | string | null,
  clase = 'dato',
): HTMLElement | null {
  if (valor === null) return null;
  return el('div', { clase }, [
    el('span', { clase: 'dato-etiqueta', texto: etiqueta }),
    typeof valor === 'string' ? el('span', { clase: 'dato-valor', texto: valor }) : valor,
  ]);
}

/**
 * "Se muestran las N primeras — hay mas".
 *
 * ---------------------------------------------------------------------------
 * UNA SOLA FUNCION PARA LAS CUATRO LISTAS, Y ESO ES EL PUNTO
 *
 * Cuatro pantallas recortan su lista: solicitudes de RFQ, cola de altas,
 * cotizaciones y directorio de proveedores. La regla que las gobierna es la
 * misma que ya estaba escrita para el autocompletado: un recorte que no se
 * declara es indistinguible de una respuesta completa, y quien lo lee concluye
 * que ha visto todo lo que hay.
 *
 * Con 363 solicitudes cargadas y un tope de 50, eso son 313 que un operador da
 * por atendidas sin haberlas visto.
 *
 * Vive en `dom.ts` --y no repetida en cada vista-- porque cuatro copias de un
 * aviso son cuatro redacciones que se separan, y la que se quede vieja va a ser
 * justo la de la pantalla que menos se mira.
 *
 * Devuelve `null` cuando no sobra nada: una lista completa no gasta una linea
 * en decir que esta completa.
 * ---------------------------------------------------------------------------
 */
export function avisoRecorte(
  hayMas: boolean,
  cuantas: number,
  texto: (n: string) => string,
): HTMLElement | null {
  if (!hayMas) return null;
  return el('p', {
    clase: 'aviso aviso-neutro aviso-recorte',
    // `role="status"`: aparece y desaparece con la lista, y quien la escucha
    // tiene que enterarse sin que el foco se mueva.
    attrs: { role: 'status' },
    texto: texto(String(cuantas)),
  });
}

export function vaciar(nodo: HTMLElement): HTMLElement {
  nodo.replaceChildren();
  return nodo;
}
